export type ContentMode = "volleyOnly" | "communityOnly" | "mixed";

export interface Filter {
    tagIds: number[];
    contentMode: ContentMode;
    position: boolean;
    appId: number[] | null;
    providerIds: number[];
    excludeProviderIds: number[];
}

// Legacy interface for migration
interface LegacyFilter {
    tagIds: number[];
    includeCommunityContent: boolean;
    position: boolean;
    appId: number[] | null;
    providerIds: number[];
}

interface FilterStateManager {
    getFilter: (key: string) => Filter | null;
    setFilter: (key: string, filter: Filter) => void;
    clearFilter: (key?: string) => void;
    getSearch: (key: string) => string | null;
    setSearch: (key: string, search: string) => void;
    clearSearch: (key?: string) => void;
}

const VOLLEY_USER_FILTER = "volley-user-filter";
const VOLLEY_USER_SEARCH = "volley-user-search";

function generateKey(base: string, key: string): string {
    return `${base}-${key}`;
}

function migrateFilter(parsed: LegacyFilter | Filter): Filter {
    // Check if this is a legacy filter with includeCommunityContent
    if ("includeCommunityContent" in parsed && !("contentMode" in parsed)) {
        const legacy = parsed as LegacyFilter;
        return {
            tagIds: legacy.tagIds,
            contentMode: legacy.includeCommunityContent
                ? "mixed"
                : "volleyOnly",
            position: legacy.position,
            appId: legacy.appId,
            providerIds: legacy.providerIds,
            excludeProviderIds: [],
        };
    }

    // Ensure excludeProviderIds exists
    const filter = parsed as Filter;
    if (!filter.excludeProviderIds) {
        filter.excludeProviderIds = [];
    }

    return filter;
}

const FilterState: FilterStateManager = {
    getFilter: (key: string) => {
        const objectMaybe = sessionStorage.getItem(
            generateKey(VOLLEY_USER_FILTER, key),
        );
        if (objectMaybe) {
            const parsed = JSON.parse(objectMaybe);
            return migrateFilter(parsed);
        }

        return null;
    },
    setFilter: (key: string, filter: Filter) => {
        const value = JSON.stringify(filter);
        sessionStorage.setItem(generateKey(VOLLEY_USER_FILTER, key), value);
    },
    clearFilter: (key?: string) => {
        if (key) {
            sessionStorage.removeItem(generateKey(VOLLEY_USER_FILTER, key));
        } else {
            for (let i = 0; i < sessionStorage.length; i += 1) {
                const keyMaybe = sessionStorage.key(i);
                if (keyMaybe && keyMaybe.startsWith(VOLLEY_USER_FILTER)) {
                    sessionStorage.removeItem(keyMaybe);
                }
            }
        }
    },
    getSearch: (key: string) =>
        sessionStorage.getItem(generateKey(VOLLEY_USER_SEARCH, key)),
    setSearch: (key: string, search: string) => {
        sessionStorage.setItem(generateKey(VOLLEY_USER_SEARCH, key), search);
    },
    clearSearch: (key?: string) => {
        if (key) {
            sessionStorage.removeItem(generateKey(VOLLEY_USER_SEARCH, key));
        } else {
            for (let i = 0; i < sessionStorage.length; i += 1) {
                const keyMaybe = sessionStorage.key(i);
                if (keyMaybe && keyMaybe.startsWith(VOLLEY_USER_SEARCH)) {
                    sessionStorage.removeItem(keyMaybe);
                }
            }
        }
    },
};

export default FilterState;
